/**
 * @file usePageRevisions.ts
 * @description История версий страницы (phase19.16 Р3а; SB126, SB63;
 * `GET pages/:id/revisions?before=&limit=`): «Последние» в панели
 * «Публикации» (3 строки) и «Все версии» (по 20, «Показать ещё»).
 * Перечитывается, когда меняется версия на сайте (`liveVersion`).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { sitePagePublish, type SitePageRevisionItem } from "../../lib/api-site-page-publish";

/** Текст ошибки по проекту (uiux-r3 §3.1). */
export const REVISIONS_FAILED = "Не удалось загрузить версии. Черновик и сайт в порядке.";

export interface PageRevisionsState {
  items: SitePageRevisionItem[];
  total: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  reload: () => void;
  loadMore: () => void;
}

export function usePageRevisions(pageId: string | null, limit: number, liveVersion: number | null): PageRevisionsState {
  const [items, setItems] = useState<SitePageRevisionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = useRef(pageId);
  current.current = pageId;

  const fetchPage = useCallback(
    async (before: number | undefined) => {
      const id = current.current;
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await sitePagePublish.revisions(id, { limit, ...(before !== undefined ? { before } : {}) });
        if (current.current !== id) return;
        setItems((prev) => (before === undefined ? res.items : [...prev, ...res.items]));
        setTotal(res.total);
      } catch {
        if (current.current === id) setError(REVISIONS_FAILED);
      } finally {
        if (current.current === id) setLoading(false);
      }
    },
    [limit],
  );

  useEffect(() => {
    setItems([]);
    setTotal(0);
    if (pageId) void fetchPage(undefined);
  }, [pageId, liveVersion, fetchPage]);

  const last = items[items.length - 1];
  return {
    items,
    total,
    loading,
    error,
    hasMore: items.length < total,
    reload: () => void fetchPage(undefined),
    loadMore: () => {
      if (last) void fetchPage(last.version);
    },
  };
}
